import { safeNumber } from '../../lib/number';
import type { Candle, ChartInterval } from '../../types/market';
import { BYBIT_INTERVAL } from './bybit';
import type { KlineStreamState, KlineStreamStatus } from './binanceKlineStream';

interface BybitKlineItem {
  start: number;
  end: number;
  interval: string;
  open: string;
  close: string;
  high: string;
  low: string;
  volume: string;
  turnover?: string;
  confirm: boolean;
  timestamp?: number;
}

interface BybitKlinePayload {
  topic?: string;
  ts?: number;
  data?: BybitKlineItem[];
  success?: boolean;
  ret_msg?: string;
}

interface SubscribeKlineOptions {
  onStatus?: (status: KlineStreamStatus) => void;
}

export function subscribeBybitKline(
  symbol: string,
  interval: ChartInterval,
  marketKind: 'spot' | 'futures',
  onUpdate: (update: { candle: Candle; eventTime: number }) => void,
  options: SubscribeKlineOptions = {},
): () => void {
  let stopped = false;
  let socket: WebSocket | undefined;
  let retryTimer: number | undefined;
  let retries = 0;
  let validEvents = 0;
  const endpoint = marketKind === 'spot'
    ? 'wss://stream.bybit.com/v5/public/spot'
    : 'wss://stream.bybit.com/v5/public/linear';
  const topic = `kline.${BYBIT_INTERVAL[interval]}.${symbol}`;

  const publish = (state: KlineStreamState, message: string): void => {
    options.onStatus?.({ state, message, retries, endpoint });
  };

  const scheduleReconnect = (): void => {
    if (stopped) return;
    retries += 1;
    if (validEvents === 0 && retries >= 6) {
      publish('unavailable', `Bybit live ${interval} stream is unavailable after repeated attempts.`);
      return;
    }
    const delay = Math.min(20_000, 750 * 2 ** Math.min(retries, 5));
    publish('reconnecting', `Bybit live ${interval} stream reconnecting in ${(delay / 1_000).toFixed(1)}s.`);
    retryTimer = window.setTimeout(connect, delay);
  };

  const connect = (): void => {
    if (stopped) return;
    publish(retries > 0 ? 'reconnecting' : 'connecting', `Connecting Bybit live ${interval} candle stream.`);
    socket = new WebSocket(endpoint);
    const openTimeout = window.setTimeout(() => {
      if (socket?.readyState !== WebSocket.OPEN) socket?.close();
    }, 8_000);

    socket.addEventListener('open', () => {
      window.clearTimeout(openTimeout);
      socket?.send(JSON.stringify({ op: 'subscribe', args: [topic] }));
      publish('connecting', `Bybit candle socket connected; awaiting the first verified ${interval} market event.`);
    });

    socket.addEventListener('message', (event) => {
      try {
        const payload = JSON.parse(String(event.data)) as BybitKlinePayload;
        if (payload.success === false) {
          publish('reconnecting', `Bybit rejected the candle subscription: ${payload.ret_msg ?? 'unknown error'}.`);
          socket?.close();
          return;
        }
        if (payload.topic !== topic) return;
        for (const item of payload.data ?? []) {
          const firstValidEvent = validEvents === 0;
          validEvents += 1;
          retries = 0;
          if (firstValidEvent) publish('live', `Bybit live ${interval} candle events verified.`);
          onUpdate({
            candle: {
              openTime: safeNumber(item.start),
              closeTime: safeNumber(item.end),
              open: safeNumber(item.open),
              high: safeNumber(item.high),
              low: safeNumber(item.low),
              close: safeNumber(item.close),
              volume: safeNumber(item.volume),
              quoteVolume: safeNumber(item.turnover),
              closed: Boolean(item.confirm),
            },
            eventTime: safeNumber(item.timestamp ?? payload.ts, Date.now()),
          });
        }
      } catch (error) {
        publish(validEvents > 0 ? 'live' : 'connecting', `A malformed Bybit candle event was ignored: ${error instanceof Error ? error.message : 'invalid payload'}.`);
      }
    });

    socket.addEventListener('close', () => {
      window.clearTimeout(openTimeout);
      if (!stopped) scheduleReconnect();
    });
    socket.addEventListener('error', () => socket?.close());
  };

  connect();
  return () => {
    stopped = true;
    if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    socket?.close();
  };
}
